import { Injectable } from '@angular/core';
import type { Feature, FeatureCollection, Point } from 'geojson';
import mapboxgl from 'mapbox-gl';
import {
  NOTE_PIN_REFERENCE_ZOOM,
  pinIconSizeMapboxExpression,
  pinTipIconOffset,
} from './note-pin-layer.util';

const NOTE_PIN_IMAGE_ID = 'doktar-note-pin';
const NOTE_PSD_IMAGE_ID = 'doktar-note-psd-pin';
const NOTE_PIN_URL = 'assets/svg/note-pin.svg';
const NOTE_PSD_URL = 'assets/svg/note-psd-pin.svg';
const PIN_IMAGE_PIXEL_RATIO = 2;

export const NOTE_PINS_BATCH_SOURCE = 'doktar-note-pins-batch-source';
export const NOTE_PINS_BATCH_LAYER = 'doktar-note-pins-batch-layer';
export const NOTE_PIN_DRAFT_SOURCE = 'doktar-note-pin-draft-source';
export const NOTE_PIN_DRAFT_LAYER = 'doktar-note-pin-draft-layer';

const EMPTY: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] };

/** Tip position in SVG space for `assets/svg/note-pin.svg` and `note-psd-pin.svg` (files unchanged). */
const NOTE_PIN_LOGICAL = { width: 44, height: 49, tipX: 19.26, tipY: 41 };
const NOTE_PSD_LOGICAL = { width: 32, height: 32, tipX: 16.18, tipY: 30.33 };

/** Screen-pixel size at {@link NOTE_PIN_REFERENCE_ZOOM}; layer `icon-size` scales with zoom (see {@link PIN_GROWTH_PER_ZOOM}). */
const BATCH_NOMINAL_PX = 40;
const BATCH_MIN_PX = 8;
const BATCH_MAX_PX = 96;
const DRAFT_NOMINAL_PX = 50;
const DRAFT_MIN_PX = 10;
const DRAFT_MAX_PX = 110;
/** Halves/doubles pin screen size per zoom level out/in (Mapbox scales on GPU; no GeoJSON refresh on zoom). */
const PIN_GROWTH_PER_ZOOM = 2;

export interface NotePinBatchItem {
  noteId: string;
  lng: number;
  lat: number;
  isProblematicSpot: boolean;
}

export interface NotePinLayerContext {
  getMap: () => mapboxgl.Map | undefined;
  getBeforeLabelLayerId: () => string | undefined;
}

/**
 * Crop-health note pins as Mapbox symbol layers (same pattern as cropmap MapPinLayerService).
 * Uses existing note SVG assets rasterized once; batch layer for listed notes, draft layer for add-note placement.
 */
@Injectable({ providedIn: 'root' })
export class NotePinLayerService {
  private context: NotePinLayerContext | null = null;
  private imagesLoaded: Promise<void> | null = null;
  private notePinWidthPx = NOTE_PIN_LOGICAL.width;
  private notePsdWidthPx = NOTE_PSD_LOGICAL.width;
  private batchClickRegistered = false;
  private batchClickHandler: ((e: mapboxgl.MapLayerMouseEvent) => void) | undefined;
  private batchMouseEnter: (() => void) | undefined;
  private batchMouseLeave: (() => void) | undefined;
  private noteClickCallback: ((noteId: string) => void) | undefined;

  configure(context: NotePinLayerContext): void {
    this.context = context;
  }

  setNoteClickHandler(handler: (noteId: string) => void): void {
    this.noteClickCallback = handler;
  }

  /** Clears layers, listeners, and context when the map view is destroyed. */
  dispose(): void {
    this.detachBatchClick();
    this.removeLayersAndSources();
    this.imagesLoaded = null;
    this.noteClickCallback = undefined;
    this.context = null;
  }

  /** Clears visible note pins; does not touch the add-note draft pin source. */
  hideBatch(): void {
    this.setSourceData(NOTE_PINS_BATCH_SOURCE, EMPTY);
  }

  /** Renders note pins from GeoJSON; visibility at zoom is handled by symbol-layer `icon-size`, not by hiding the layer. */
  showBatch(items: NotePinBatchItem[]): void {
    const map = this.context?.getMap();
    if (!map || items.length === 0) {
      this.hideBatch();
      return;
    }

    void this.ensureImagesLoaded()
      .then(() => {
        const currentMap = this.context?.getMap();
        if (!currentMap) {
          return;
        }
        const features = items.map((item) => this.buildBatchFeature(item));
        this.ensureBatchLayer();
        this.registerBatchClick();
        this.setSourceData(NOTE_PINS_BATCH_SOURCE, { type: 'FeatureCollection', features });
      })
      .catch((err) => console.error('[NotePinLayerService] note pin image load failed', err));
  }

  /** Removes the single pin shown while the user is placing a new note. */
  clearDraft(): void {
    this.setSourceData(NOTE_PIN_DRAFT_SOURCE, EMPTY);
  }

  /** Shows the add-note pin on a separate source so {@link hideBatch} does not clear it. */
  setDraft(lng: number, lat: number): void {
    if (Number.isNaN(lng) || Number.isNaN(lat)) {
      this.clearDraft();
      return;
    }

    const map = this.context?.getMap();
    if (!map) {
      return;
    }

    void this.ensureImagesLoaded()
      .then(() => {
        const currentMap = this.context?.getMap();
        if (!currentMap) {
          return;
        }
        this.ensureDraftLayer();
        const feature: Feature<Point> = {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {},
        };
        this.setSourceData(NOTE_PIN_DRAFT_SOURCE, { type: 'FeatureCollection', features: [feature] });
      })
      .catch((err) => console.error('[NotePinLayerService] draft pin image load failed', err));
  }

  /** Keeps batch/draft pins above the satellite raster and below basemap labels. */
  ensureStackOrder(rasterLayerId?: string): void {
    const map = this.context?.getMap();
    if (!map) {
      return;
    }
    const beforeLabel = this.context?.getBeforeLabelLayerId();
    for (const layerId of [NOTE_PINS_BATCH_LAYER, NOTE_PIN_DRAFT_LAYER]) {
      if (map.getLayer(layerId)) {
        map.moveLayer(layerId, beforeLabel);
      }
    }
    if (rasterLayerId && map.getLayer(rasterLayerId) && map.getLayer(NOTE_PINS_BATCH_LAYER)) {
      map.moveLayer(rasterLayerId, NOTE_PINS_BATCH_LAYER);
    } else if (rasterLayerId && map.getLayer(rasterLayerId)) {
      map.moveLayer(rasterLayerId, beforeLabel);
    }
  }

  private buildBatchFeature(item: NotePinBatchItem): Feature<Point> {
    return {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [item.lng, item.lat] },
      properties: {
        noteId: item.noteId,
        iconKind: item.isProblematicSpot ? 'psd' : 'regular',
      },
    };
  }

  private ensureImagesLoaded(): Promise<void> {
    const map = this.context?.getMap();
    if (!map) {
      return Promise.resolve();
    }
    if (!map.hasImage(NOTE_PIN_IMAGE_ID) || !map.hasImage(NOTE_PSD_IMAGE_ID)) {
      this.imagesLoaded = null;
    }
    if (this.imagesLoaded) {
      return this.imagesLoaded;
    }

    this.imagesLoaded = Promise.all([
      this.loadSvgImageData(NOTE_PIN_URL),
      this.loadSvgImageData(NOTE_PSD_URL),
    ])
      .then(([notePin, notePsd]) => {
        const currentMap = this.context?.getMap();
        if (!currentMap) {
          return;
        }
        if (!currentMap.hasImage(NOTE_PIN_IMAGE_ID)) {
          currentMap.addImage(NOTE_PIN_IMAGE_ID, notePin.data, { pixelRatio: PIN_IMAGE_PIXEL_RATIO });
        }
        if (!currentMap.hasImage(NOTE_PSD_IMAGE_ID)) {
          currentMap.addImage(NOTE_PSD_IMAGE_ID, notePsd.data, { pixelRatio: PIN_IMAGE_PIXEL_RATIO });
        }
        this.notePinWidthPx = notePin.width > 0 ? notePin.width : NOTE_PIN_LOGICAL.width;
        this.notePsdWidthPx = notePsd.width > 0 ? notePsd.width : NOTE_PSD_LOGICAL.width;
      })
      .catch((err) => {
        this.imagesLoaded = null;
        throw err;
      });
    return this.imagesLoaded;
  }

  /** Mapbox cannot decode SVG via `loadImage`; rasterize in canvas without modifying the asset file. */
  private loadSvgImageData(url: string): Promise<{ data: ImageData; width: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const width = img.naturalWidth > 0 ? img.naturalWidth : 44;
          const height = img.naturalHeight > 0 ? img.naturalHeight : 49;
          const canvas = document.createElement('canvas');
          canvas.width = width * PIN_IMAGE_PIXEL_RATIO;
          canvas.height = height * PIN_IMAGE_PIXEL_RATIO;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas 2d unavailable'));
            return;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve({ data: ctx.getImageData(0, 0, canvas.width, canvas.height), width });
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error(`Failed to load pin image: ${url}`));
      img.src = url;
    });
  }

  private batchIconSizeExpression(): mapboxgl.ExpressionSpecification {
    const wRegular = this.notePinWidthPx > 0 ? this.notePinWidthPx : NOTE_PIN_LOGICAL.width;
    const wPsd = this.notePsdWidthPx > 0 ? this.notePsdWidthPx : NOTE_PSD_LOGICAL.width;
    const regular = pinIconSizeMapboxExpression(
      BATCH_NOMINAL_PX / wRegular,
      NOTE_PIN_REFERENCE_ZOOM,
      BATCH_MIN_PX / wRegular,
      BATCH_MAX_PX / wRegular,
      PIN_GROWTH_PER_ZOOM,
    );
    const psd = pinIconSizeMapboxExpression(
      BATCH_NOMINAL_PX / wPsd,
      NOTE_PIN_REFERENCE_ZOOM,
      BATCH_MIN_PX / wPsd,
      BATCH_MAX_PX / wPsd,
      PIN_GROWTH_PER_ZOOM,
    );
    return ['match', ['get', 'iconKind'], 'psd', psd, regular];
  }

  private draftIconSizeExpression(): mapboxgl.ExpressionSpecification {
    const w = this.notePinWidthPx > 0 ? this.notePinWidthPx : NOTE_PIN_LOGICAL.width;
    return pinIconSizeMapboxExpression(
      DRAFT_NOMINAL_PX / w,
      NOTE_PIN_REFERENCE_ZOOM,
      DRAFT_MIN_PX / w,
      DRAFT_MAX_PX / w,
      PIN_GROWTH_PER_ZOOM,
    );
  }

  private ensureBatchLayer(): void {
    const map = this.context?.getMap();
    if (!map) {
      return;
    }
    if (!map.getSource(NOTE_PINS_BATCH_SOURCE)) {
      map.addSource(NOTE_PINS_BATCH_SOURCE, { type: 'geojson', data: EMPTY });
    }
    const beforeId = this.context?.getBeforeLabelLayerId();
    const noteOffset = pinTipIconOffset(
      NOTE_PIN_LOGICAL.width,
      NOTE_PIN_LOGICAL.height,
      NOTE_PIN_LOGICAL.tipX,
      NOTE_PIN_LOGICAL.tipY,
    );
    const psdOffset = pinTipIconOffset(
      NOTE_PSD_LOGICAL.width,
      NOTE_PSD_LOGICAL.height,
      NOTE_PSD_LOGICAL.tipX,
      NOTE_PSD_LOGICAL.tipY,
    );
    const iconOffset: mapboxgl.ExpressionSpecification = [
      'match',
      ['get', 'iconKind'],
      'psd',
      ['literal', psdOffset],
      ['literal', noteOffset],
    ];

    if (map.getLayer(NOTE_PINS_BATCH_LAYER)) {
      map.setLayoutProperty(NOTE_PINS_BATCH_LAYER, 'icon-size', this.batchIconSizeExpression());
      map.setLayoutProperty(NOTE_PINS_BATCH_LAYER, 'icon-offset', iconOffset);
      return;
    }

    map.addLayer(
      {
        id: NOTE_PINS_BATCH_LAYER,
        type: 'symbol',
        source: NOTE_PINS_BATCH_SOURCE,
        layout: {
          'icon-image': ['match', ['get', 'iconKind'], 'psd', NOTE_PSD_IMAGE_ID, NOTE_PIN_IMAGE_ID],
          'icon-anchor': 'bottom',
          'icon-offset': iconOffset,
          'icon-allow-overlap': true,
          'icon-size': this.batchIconSizeExpression(),
        },
      },
      beforeId,
    );
  }

  private ensureDraftLayer(): void {
    const map = this.context?.getMap();
    if (!map) {
      return;
    }
    if (!map.getSource(NOTE_PIN_DRAFT_SOURCE)) {
      map.addSource(NOTE_PIN_DRAFT_SOURCE, { type: 'geojson', data: EMPTY });
    }
    const beforeId = this.context?.getBeforeLabelLayerId();
    const iconOffset = pinTipIconOffset(
      NOTE_PIN_LOGICAL.width,
      NOTE_PIN_LOGICAL.height,
      NOTE_PIN_LOGICAL.tipX,
      NOTE_PIN_LOGICAL.tipY,
    );

    if (map.getLayer(NOTE_PIN_DRAFT_LAYER)) {
      map.setLayoutProperty(NOTE_PIN_DRAFT_LAYER, 'icon-size', this.draftIconSizeExpression());
      return;
    }

    map.addLayer(
      {
        id: NOTE_PIN_DRAFT_LAYER,
        type: 'symbol',
        source: NOTE_PIN_DRAFT_SOURCE,
        layout: {
          'icon-image': NOTE_PIN_IMAGE_ID,
          'icon-anchor': 'bottom',
          'icon-offset': iconOffset,
          'icon-allow-overlap': true,
          'icon-size': this.draftIconSizeExpression(),
        },
      },
      beforeId,
    );
  }

  private setSourceData(sourceId: string, data: FeatureCollection<Point>): void {
    const map = this.context?.getMap();
    if (!map) {
      return;
    }
    const source = map.getSource(sourceId);
    if (source && 'setData' in source) {
      (source as mapboxgl.GeoJSONSource).setData(data);
    }
  }

  private registerBatchClick(): void {
    const map = this.context?.getMap();
    if (!map || this.batchClickRegistered) {
      return;
    }

    this.batchClickHandler = (e: mapboxgl.MapLayerMouseEvent) => {
      const props = e.features?.[0]?.properties as { noteId?: string } | undefined;
      const noteId = props?.noteId;
      if (typeof noteId === 'string' && noteId.length > 0) {
        this.noteClickCallback?.(noteId);
      }
    };
    this.batchMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    this.batchMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };
    map.on('click', NOTE_PINS_BATCH_LAYER, this.batchClickHandler);
    map.on('mouseenter', NOTE_PINS_BATCH_LAYER, this.batchMouseEnter);
    map.on('mouseleave', NOTE_PINS_BATCH_LAYER, this.batchMouseLeave);
    this.batchClickRegistered = true;
  }

  private detachBatchClick(): void {
    const map = this.context?.getMap();
    if (map && this.batchClickRegistered) {
      if (this.batchClickHandler) {
        map.off('click', NOTE_PINS_BATCH_LAYER, this.batchClickHandler);
      }
      if (this.batchMouseEnter) {
        map.off('mouseenter', NOTE_PINS_BATCH_LAYER, this.batchMouseEnter);
      }
      if (this.batchMouseLeave) {
        map.off('mouseleave', NOTE_PINS_BATCH_LAYER, this.batchMouseLeave);
      }
    }
    this.batchClickRegistered = false;
    this.batchClickHandler = undefined;
    this.batchMouseEnter = undefined;
    this.batchMouseLeave = undefined;
  }

  private removeLayersAndSources(): void {
    const map = this.context?.getMap();
    if (!map) {
      return;
    }
    for (const layerId of [NOTE_PINS_BATCH_LAYER, NOTE_PIN_DRAFT_LAYER]) {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    }
    for (const sourceId of [NOTE_PINS_BATCH_SOURCE, NOTE_PIN_DRAFT_SOURCE]) {
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    }
  }
}
